import { KuxMenuItem } from '@kaltura-ott/tvpil';
import { action } from 'mobx';
import { container, singleton } from 'tsyringe';

import { CategoryChildren } from '../stores/category/category-children';
import { CategoryStore } from '../stores/category/category-store';
import type { MediaAsset } from '../stores/entities/MediaAsset';
import { Subscription } from '../stores/entities/Subscription';
import type { ParentalPinStore } from '../stores/parentalPin/parental-pin-store';
import { ParentalPinType } from '../types/stores/parental-pin-store-type';

@singleton()
export class ParentalPinStoreFacade {
  originalStore: ParentalPinStore;

  constructor() {
    this.originalStore = container.resolve<ParentalPinStore>('parentalPinStore');
  }

  public get isPurchasePinLocked() {
    return this.originalStore.isPurchasePinLocked;
  }

  public get isParentalPinLocked() {
    return this.originalStore.isParentalPinLocked;
  }

  public get isAdultPinEntered() {
    return this.originalStore.isAdultPinEntered;
  }

  public get isParentalPinEntered() {
    return this.originalStore.isParentalPinEntered;
  }

  public get purchasePinTimeout() {
    return this.originalStore.purchasePinTimeout;
  }

  public get parentalPinTimeout() {
    return this.originalStore.parentalPinTimeout;
  }

  public get purchasePinEntered() {
    return this.originalStore.purchasePinEntered;
  }

  public get ageThreshold() {
    return this.originalStore.ageThreshold;
  }

  public get pinForPurchase() {
    return this.originalStore.pinForPurchase;
  }

  public get noThresholdAge() {
    return this.originalStore.noThresholdAge;
  }

  public init() {
    return this.originalStore.init();
  }

  public isBlockByParentRating<T extends MediaAsset | CategoryStore<CategoryChildren>>(asset: T) {
    return this.originalStore.isBlockByParentRating(asset);
  }

  public isBlockedByParentalRating(parentalRating: number) {
    return this.originalStore.isBlockedByParentalRating(parentalRating);
  }

  public isBlockByAdult<T extends MediaAsset | Subscription | CategoryStore<CategoryChildren>>(asset: T) {
    return this.originalStore.isBlockByAdult(asset);
  }

  @action.bound
  public clearPinCodes() {
    return this.originalStore.clearPinCodes();
  }

  @action.bound
  async updateParentalPin(pin: string) {
    return this.originalStore.updateParentalPin(pin);
  }

  @action.bound
  async updatePurchasePin(pin: string) {
    return this.originalStore.updatePurchasePin(pin);
  }

  @action.bound
  async validatePurchasePin(pin: string) {
    return this.originalStore.validatePurchasePin(pin);
  }

  @action.bound
  async checkMenuParentalPin(menuItem: KuxMenuItem) {
    return this.originalStore.checkMenuParentalPin(menuItem);
  }

  @action.bound
  async clearParentalPin(type: ParentalPinType) {
    return this.originalStore.clearParentalPin(type);
  }

  @action.bound
  async initModal() {
    return this.originalStore.initModal();
  }

  @action.bound
  async validatePin<T extends MediaAsset | Subscription | CategoryStore<CategoryChildren>>(
    pin: string,
    type: ParentalPinType,
    asset: T,
  ) {
    return this.originalStore.validatePin(pin, type, asset);
  }

  @action.bound
  async validateParentalRating(args: { pin: string; parentalRating: number }) {
    return this.originalStore.validateParentalRating(args);
  }

  @action.bound
  async validateAdultPin(pin: string) {
    return this.originalStore.validateAdultPin(pin);
  }

  @action.bound
  async validateInApplicationLink(pin: string, link: string) {
    return this.originalStore.validateInApplicationLink(pin, link);
  }

  @action.bound
  /**
   * Destroy timer which is tracking lock time
   */
  public destroy() {
    return this.originalStore.destroy();
  }
}
